import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { bulkSetReportingSync, type BulkAssignManagerResult } from './api';

// Build A — bulk reporting-sync modal. Admin-only (gated by the caller). Enrols
// or removes the selected accounts from the recurring Meta reporting pull via
// the bulk endpoint; surfaces the {updated, errors} result before closing.
// Mirrors AccountsBulkAssignModal.

type Props = {
  accountIds: string[];
  onClose: () => void;
  onDone: (result: BulkAssignManagerResult) => void;
};

export function AccountsBulkReportingSyncModal({ accountIds, onClose, onDone }: Props) {
  // null = not yet chosen; true = enable; false = disable.
  const [choice, setChoice] = useState<boolean | null>(null);
  const [result, setResult] = useState<BulkAssignManagerResult | null>(null);

  const mut = useMutation({
    mutationFn: () => bulkSetReportingSync(accountIds, choice === true),
    onSuccess: (res) => {
      setResult(res);
      // Let the parent refetch + clear selection, but keep the modal open so
      // the admin sees the outcome (esp. partial errors).
      onDone(res);
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-1">Reporting sync</h2>
        <p className="text-sm text-slate-600 mb-4">
          Applies to {accountIds.length} selected account{accountIds.length === 1 ? '' : 's'}. The
          recurring Meta reporting pull syncs only enabled accounts.
        </p>

        <div className="flex gap-2">
          <label className="inline-flex flex-1 items-center gap-2 rounded border border-slate-300 px-3 py-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="reporting-sync-choice"
              checked={choice === true}
              onChange={() => setChoice(true)}
              disabled={mut.isPending}
            />
            Enable sync
          </label>
          <label className="inline-flex flex-1 items-center gap-2 rounded border border-slate-300 px-3 py-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="reporting-sync-choice"
              checked={choice === false}
              onChange={() => setChoice(false)}
              disabled={mut.isPending}
            />
            Disable sync
          </label>
        </div>

        {choice !== null && !result && (
          <p className="mt-3 text-sm text-slate-700">
            {choice ? 'Enable' : 'Disable'} reporting sync on{' '}
            <span className="font-medium">{accountIds.length}</span> account
            {accountIds.length === 1 ? '' : 's'}?
          </p>
        )}

        {result && (
          <div className="mt-3 text-sm">
            <p className="text-emerald-700">
              Updated {result.updated} account{result.updated === 1 ? '' : 's'}.
            </p>
            {result.errors.length > 0 && (
              <div className="mt-2 text-rose-600">
                <p>{result.errors.length} failed:</p>
                <ul className="list-disc pl-5 max-h-32 overflow-y-auto">
                  {result.errors.map((e) => (
                    <li key={e.accountId}>
                      {e.accountId}: {e.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {mut.isError && (
          <p className="mt-3 text-sm text-rose-600">
            {(mut.error as Error).message || 'Failed to update. Try again.'}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          {result ? (
            <Button onClick={onClose}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
                Cancel
              </Button>
              <Button onClick={() => mut.mutate()} disabled={choice === null || mut.isPending}>
                {mut.isPending ? 'Saving…' : 'Apply'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
